import { getLoadingLabelRuntimeSync } from "../loading-label-runtime-loader";
import { createElement, createNode } from "../utils/dom";
import {
  AgentWidgetMessage,
  AgentWidgetMessageRole,
  AgentWidgetMessageLayoutConfig,
  AgentWidgetAvatarConfig,
  AgentWidgetTimestampConfig,
  AgentWidgetMessageActionsConfig,
  AgentWidgetCustomMessageAction,
  AgentWidgetMessageFeedback,
  LoadingIndicatorRenderContext,
  ImageContentPart,
  AudioContentPart,
  VideoContentPart,
  FileContentPart,
  AgentWidgetContextMentionRef,
  AgentWidgetContentSegment,
  AgentWidgetContextMentionTokenRenderContext,
  StopReasonKind
} from "../types";
import type { MessageGeometryTokens } from "../types/theme";
import { resolveThemeDefaults } from "../utils/tokens";
import { createMentionTokenElement } from "../utils/mention-token";
import { createIconButton } from "../utils/buttons";
import { renderLucideIcon } from "../utils/icons";
import { IMAGE_ONLY_MESSAGE_FALLBACK_TEXT } from "../utils/content";
import {
  applyUserCollapseState,
  createUserCollapseRegion,
  type UserMessageCollapseRender,
} from "./user-message-collapse";
import {
  applyStreamBuffer,
  createSkeletonPlaceholder,
  createStreamCaret,
  resolveStreamAnimation,
  resolveStreamAnimationPlugin,
  wrapStreamAnimation,
} from "../utils/stream-animation";

/**
 * Default copy for the inline notice rendered when a turn ends with a
 * non-natural stop reason. Deployers override per-reason via
 * `config.copy.stopReasonNotice`. Returns `null` for natural completions
 * (`end_turn`) and uninformative reasons (`unknown`): those never render
 * an affordance.
 */
export const getDefaultStopReasonNoticeCopy = (
  stopReason: StopReasonKind
): string | null => {
  switch (stopReason) {
    case "max_tool_calls":
      // The tool budget may be exhausted before a final, tool-free iteration.
      // This message-level reason does not establish that the whole run stopped.
      return "Tool limit reached.";
    case "length":
      return "Output token limit reached.";
    case "content_filter":
      return "The provider filtered this response.";
    case "error":
      return "Something went wrong generating this response.";
    default:
      return null;
  }
};

/**
 * Resolve the notice text for a stop reason, applying user overrides on
 * top of the built-in defaults. Returns `null` when the reason does not
 * warrant a notice or when the resolved string is empty (deployers can
 * suppress per-reason by setting an empty override).
 */
export const resolveStopReasonNoticeText = (
  stopReason: StopReasonKind | undefined,
  overrides?: Partial<Record<StopReasonKind, string>>
): string | null => {
  if (!stopReason) return null;
  const fallback = getDefaultStopReasonNoticeCopy(stopReason);
  // Reasons without a default (end_turn, unknown) never render: overrides
  // for those keys are intentionally ignored.
  if (fallback === null) return null;
  const override = overrides?.[stopReason];
  return (override === undefined ? fallback : override) || null;
};

/**
 * Build the inline notice element rendered on assistant bubbles whose
 * turn ended with `max_tool_calls`, `length`, `content_filter`, or `error`.
 */
const createStopReasonNotice = (
  stopReason: StopReasonKind,
  text: string
): HTMLElement => createNode("div", {
  className: "persona-message-stop-reason persona-text-xs persona-mt-2 persona-italic",
  attrs: { "data-stop-reason": stopReason, role: "note" },
  style: { opacity: "0.75" },
  text,
});

/** Validate that an image src URL uses a safe scheme (blocks javascript: and SVG data URIs). */
export const isSafeImageSrc = (src: string): boolean => {
  const lower = src.toLowerCase();
  if (lower.startsWith("data:image/svg+xml")) return false;
  if (/^(?:https?|blob):/i.test(src)) return true;
  if (lower.startsWith("data:image/")) return true;
  // Relative URLs are safe
  if (!src.includes(":")) return true;
  return false;
};

/**
 * Validate that a media src URL (audio/video/file) uses a safe scheme.
 * Allows http(s), blob:, and inert data: URIs. Blocks `javascript:` and the
 * executable data: types whose payloads a browser would render or run when a
 * user right-clicks the link → "Open in new tab" (bypassing `download`):
 * `data:text/html`, `data:text/javascript`, `data:text/xml`,
 * `data:application/xhtml`, and `data:image/svg+xml`. Inert text payloads
 * (`text/plain`, `text/csv`, `text/markdown`) remain allowed so attachments
 * with those mime types render as download links instead of vanishing.
 */
export const isSafeMediaSrc = (src: string): boolean => {
  const lower = src.toLowerCase();
  if (lower.startsWith("javascript:")) return false;
  if (lower.startsWith("data:text/html")) return false;
  if (lower.startsWith("data:text/javascript")) return false;
  if (lower.startsWith("data:text/xml")) return false;
  if (lower.startsWith("data:application/xhtml")) return false;
  if (lower.startsWith("data:image/svg+xml")) return false;
  if (/^(?:https?|blob):/i.test(src)) return true;
  if (lower.startsWith("data:")) return true;
  // Relative URLs are safe
  if (!src.includes(":")) return true;
  return false;
};

export type LoadingIndicatorRenderer = (context: LoadingIndicatorRenderContext) => HTMLElement | null;

export type MessageTransform = (context: {
  text: string;
  message: AgentWidgetMessage;
  streaming: boolean;
  raw?: string;
}) => string;

export type MessageActionCallbacks = {
  onCopy?: (message: AgentWidgetMessage) => void;
  onFeedback?: (feedback: AgentWidgetMessageFeedback) => void;
};

const MESSAGE_IMAGE_PREVIEW_MAX_WIDTH_PX = 320;
const MESSAGE_IMAGE_PREVIEW_MAX_HEIGHT_PX = 320;

const getMessageImageParts = (message: AgentWidgetMessage): ImageContentPart[] => {
  if (!message.contentParts || message.contentParts.length === 0) {
    return [];
  }

  return message.contentParts.filter(
    (part): part is ImageContentPart =>
      part.type === "image" &&
      typeof part.image === "string" &&
      part.image.trim().length > 0
  );
};

const getMessageAudioParts = (message: AgentWidgetMessage): AudioContentPart[] => {
  if (!message.contentParts || message.contentParts.length === 0) return [];
  return message.contentParts.filter(
    (part): part is AudioContentPart =>
      part.type === "audio" &&
      typeof part.audio === "string" &&
      part.audio.trim().length > 0
  );
};

const getMessageVideoParts = (message: AgentWidgetMessage): VideoContentPart[] => {
  if (!message.contentParts || message.contentParts.length === 0) return [];
  return message.contentParts.filter(
    (part): part is VideoContentPart =>
      part.type === "video" &&
      typeof part.video === "string" &&
      part.video.trim().length > 0
  );
};

const getMessageFileParts = (message: AgentWidgetMessage): FileContentPart[] => {
  if (!message.contentParts || message.contentParts.length === 0) return [];
  return message.contentParts.filter(
    (part): part is FileContentPart =>
      part.type === "file" &&
      typeof part.data === "string" &&
      part.data.trim().length > 0
  );
};

/**
 * Read-only context-mention pills for a sent user bubble (from
 * `message.contextMentions`). Same compact pill look as the composer chips, but
 * no spinner / remove control. Returns null when the message carries no mentions.
 */
const createMessageMentionChips = (
  mentions: AgentWidgetContextMentionRef[] | undefined
): HTMLElement | null => {
  if (!mentions || mentions.length === 0) return null;
  const row = createNode("div", {
    className: "persona-mention-context-row persona-message-mentions",
    attrs: { "data-message-mentions": "" },
  });
  row.style.display = "flex";
  for (const ref of mentions) {
    const chip = createNode("div", {
      className: "persona-mention-chip persona-mention-chip-readonly",
      attrs: { "data-status": "ready", title: ref.label },
    });
    const iconHost = createElement("span", "persona-mention-chip-icon");
    const icon = renderLucideIcon(ref.iconName ?? "at-sign", 13, "currentColor", 2);
    if (icon) iconHost.appendChild(icon);
    chip.appendChild(iconHost);
    chip.appendChild(
      createNode("span", { className: "persona-mention-chip-label", text: ref.label })
    );
    row.appendChild(chip);
  }
  return row;
};

/**
 * Render a sent user bubble's prose with atomic mention tokens in place (inline
 * display mode, from `message.contentSegments`). Text runs become text nodes;
 * mention segments become read-only pill tokens (shared with the composer, so a
 * host `renderMentionToken` + per-item `color` apply here too). Display/transcript
 * concern only — the model saw the resolved bodies via `llmContent`. Returns a
 * document fragment for `replaceChildren`.
 */
export const createMessageInlineMentions = (
  segments: AgentWidgetContentSegment[],
  render?: (
    ctx: AgentWidgetContextMentionTokenRenderContext
  ) => HTMLElement
): DocumentFragment => {
  const frag = document.createDocumentFragment();
  for (const seg of segments) {
    if (seg.kind === "text") {
      frag.appendChild(document.createTextNode(seg.text));
      continue;
    }
    frag.appendChild(
      createMentionTokenElement(seg.ref, { readonly: true, render })
    );
  }
  return frag;
};

/* PUA sentinels survive escapeHtml, markdown, and DOMPurify as plain text. */
const MENTION_PH_OPEN = "\uE000";
const MENTION_PH_CLOSE = "\uE001";

/**
 * Render segmented prose through the full message transform (actions, markdown,
 * postprocess, sanitize): mention slots ride the pipeline as placeholder
 * sentinels and are swapped for atomic token elements in the parsed output.
 * Falls back to verbatim segment rendering when a transform drops or rewrites
 * a slot, so a mention is never lost.
 */
export const renderSegmentsWithTransform = (
  contentDiv: HTMLElement,
  segments: AgentWidgetContentSegment[],
  transformSource: (source: string) => string,
  render?: (
    ctx: AgentWidgetContextMentionTokenRenderContext
  ) => HTMLElement
): void => {
  // Nonce keeps user-typed sentinel lookalikes from matching a real slot.
  const nonce = Math.random().toString(36).slice(2, 8);
  const placeholder = (i: number) =>
    `${MENTION_PH_OPEN}${nonce}:${i}${MENTION_PH_CLOSE}`;
  const source = segments
    .map((seg, i) => (seg.kind === "text" ? seg.text : placeholder(i)))
    .join("");
  const html = transformSource(source);
  // String-level check runs before the parse: a slot that was dropped,
  // entity-encoded, or split by an inserted tag fails `includes`.
  const survived = segments.every(
    (seg, i) => seg.kind === "text" || html.includes(placeholder(i))
  );
  if (!survived) {
    contentDiv.replaceChildren(createMessageInlineMentions(segments, render));
    return;
  }
  contentDiv.innerHTML = html;
  const pattern = new RegExp(
    `${MENTION_PH_OPEN}${nonce}:(\\d+)${MENTION_PH_CLOSE}`
  );
  const walker = document.createTreeWalker(contentDiv, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    textNodes.push(n as Text);
  }
  for (const node of textNodes) {
    let current = node;
    for (let match = pattern.exec(current.data); match; match = pattern.exec(current.data)) {
      const seg = segments[Number(match[1])];
      const after = current.splitText(match.index);
      after.data = after.data.slice(match[0].length);
      if (seg?.kind === "mention") {
        after.parentNode?.insertBefore(
          createMentionTokenElement(seg.ref, { readonly: true, render }),
          after
        );
      }
      current = after;
    }
  }
};

const createMessageImagePreviews = (
  imageParts: ImageContentPart[],
  hasVisibleText: boolean,
  onPreviewFailed?: () => void
): HTMLElement | null => {
  if (imageParts.length === 0) return null;

  try {
    const container = createElement(
      "div",
      "persona-flex persona-flex-col persona-gap-2"
    );
    container.setAttribute("data-message-attachments", "images");
    if (hasVisibleText) {
      container.style.marginBottom = "8px";
    }

    let visiblePreviewCount = 0;
    let failureHandled = false;

    const handleTotalPreviewFailure = () => {
      if (failureHandled) return;
      failureHandled = true;
      container.remove();
      onPreviewFailed?.();
    };

    imageParts.forEach((imagePart, index) => {
      const imageElement = createElement("img") as HTMLImageElement;
      imageElement.alt = imagePart.alt?.trim() || `Attached image ${index + 1}`;
      imageElement.loading = "lazy";
      imageElement.decoding = "async";
      imageElement.referrerPolicy = "no-referrer";
      imageElement.style.display = "block";
      imageElement.style.width = "100%";
      imageElement.style.maxWidth = `${MESSAGE_IMAGE_PREVIEW_MAX_WIDTH_PX}px`;
      imageElement.style.maxHeight = `${MESSAGE_IMAGE_PREVIEW_MAX_HEIGHT_PX}px`;
      imageElement.style.height = "auto";
      imageElement.style.objectFit = "contain";
      imageElement.style.borderRadius = "10px";
      imageElement.style.backgroundColor = "var(--persona-attachment-image-bg, var(--persona-container, #f3f4f6))";
      imageElement.style.border = "1px solid var(--persona-attachment-image-border, var(--persona-border, #e5e7eb))";

      let settled = false;
      visiblePreviewCount += 1;
      imageElement.addEventListener("error", () => {
        if (settled) return;
        settled = true;
        visiblePreviewCount = Math.max(0, visiblePreviewCount - 1);
        imageElement.remove();
        if (visiblePreviewCount === 0) {
          handleTotalPreviewFailure();
        }
      });
      imageElement.addEventListener("load", () => {
        settled = true;
      });

      if (isSafeImageSrc(imagePart.image)) {
        imageElement.src = imagePart.image;
        container.appendChild(imageElement);
      } else {
        // Treat blocked images like load errors so fallback triggers correctly
        settled = true;
        visiblePreviewCount = Math.max(0, visiblePreviewCount - 1);
        imageElement.remove();
      }
    });

    if (visiblePreviewCount === 0) {
      handleTotalPreviewFailure();
      return null;
    }

    return container;
  } catch {
    onPreviewFailed?.();
    return null;
  }
};

const createMessageAudioPreviews = (
  audioParts: AudioContentPart[]
): HTMLElement | null => {
  if (audioParts.length === 0) return null;
  try {
    const container = createElement(
      "div",
      "persona-flex persona-flex-col persona-gap-2"
    );
    container.setAttribute("data-message-attachments", "audio");
    let visible = 0;
    audioParts.forEach((part) => {
      if (!isSafeMediaSrc(part.audio)) return;
      const audioElement = createElement("audio") as HTMLAudioElement;
      audioElement.controls = true;
      audioElement.preload = "metadata";
      audioElement.src = part.audio;
      audioElement.style.display = "block";
      audioElement.style.width = "100%";
      audioElement.style.maxWidth = `${MESSAGE_IMAGE_PREVIEW_MAX_WIDTH_PX}px`;
      container.appendChild(audioElement);
      visible += 1;
    });
    if (visible === 0) {
      container.remove();
      return null;
    }
    return container;
  } catch {
    return null;
  }
};

const createMessageVideoPreviews = (
  videoParts: VideoContentPart[]
): HTMLElement | null => {
  if (videoParts.length === 0) return null;
  try {
    const container = createElement(
      "div",
      "persona-flex persona-flex-col persona-gap-2"
    );
    container.setAttribute("data-message-attachments", "video");
    let visible = 0;
    videoParts.forEach((part) => {
      if (!isSafeMediaSrc(part.video)) return;
      const videoElement = createElement("video") as HTMLVideoElement;
      videoElement.controls = true;
      videoElement.preload = "metadata";
      videoElement.src = part.video;
      videoElement.style.display = "block";
      videoElement.style.width = "100%";
      videoElement.style.maxWidth = `${MESSAGE_IMAGE_PREVIEW_MAX_WIDTH_PX}px`;
      videoElement.style.maxHeight = `${MESSAGE_IMAGE_PREVIEW_MAX_HEIGHT_PX}px`;
      videoElement.style.borderRadius = "10px";
      videoElement.style.backgroundColor =
        "var(--persona-attachment-image-bg, var(--persona-container, #f3f4f6))";
      container.appendChild(videoElement);
      visible += 1;
    });
    if (visible === 0) {
      container.remove();
      return null;
    }
    return container;
  } catch {
    return null;
  }
};

const createMessageFilePreviews = (
  fileParts: FileContentPart[]
): HTMLElement | null => {
  if (fileParts.length === 0) return null;
  try {
    const container = createElement(
      "div",
      "persona-flex persona-flex-col persona-gap-2"
    );
    container.setAttribute("data-message-attachments", "files");
    let visible = 0;
    fileParts.forEach((part) => {
      if (!isSafeMediaSrc(part.data)) return;
      const link = createElement("a") as HTMLAnchorElement;
      link.href = part.data;
      link.download = part.filename;
      // Cross-origin URLs ignore the `download` attribute, so without
      // `target=_blank` the link would navigate the chat page to the file.
      // Pair with `rel="noopener noreferrer"` to prevent reverse tabnabbing.
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = part.filename;
      link.className = "persona-message-file-attachment";
      link.style.display = "inline-flex";
      link.style.alignItems = "center";
      link.style.gap = "6px";
      link.style.padding = "6px 10px";
      link.style.borderRadius = "8px";
      link.style.fontSize = "0.875rem";
      link.style.textDecoration = "underline";
      link.style.backgroundColor =
        "var(--persona-attachment-file-bg, var(--persona-container, #f3f4f6))";
      link.style.border =
        "1px solid var(--persona-attachment-file-border, var(--persona-border, #e5e7eb))";
      link.style.color = "inherit";
      container.appendChild(link);
      visible += 1;
    });
    if (visible === 0) {
      container.remove();
      return null;
    }
    return container;
  } catch {
    return null;
  }
};

// Create typing indicator element
export const createTypingIndicator = (config: import("../types").AgentWidgetConfig = {}): HTMLElement => {
  const loading = config.loadingIndicator;
  const v5 = config.future?.v5Defaults;
  const variant = loading?.variant ?? (v5 ? "label" : "dots");
  if (variant === "label") {
    const label = createNode("div", { className: "persona-loading-label", attrs: { role: "status" } });
    if (v5 && loading?.showBubble !== true) {
      label.classList.add("persona-activity-row", "persona-activity-header");
    }
    const display = config.features?.toolCallDisplay;
    const visibility = loading?.iconVisibility ?? (display?.grouped !== false && (display?.groupedMode ?? "collapsible") === "collapsible"
      ? display?.groupIconVisibility : undefined) ?? display?.iconVisibility;
    const name = loading?.iconName ?? "loader-circle";
    if (visibility !== "never" && (v5 || loading?.iconName || loading?.iconVisibility)) {
      const icon = renderLucideIcon(name, 16);
      if (icon) {
        icon.classList.add("persona-activity-icon");
        icon.dataset.loadingIconAnimation = loading?.iconAnimation ?? (name === "loader-circle" ? "spin" : "none");
        label.appendChild(icon);
      }
    }
    const text = createNode("span", { text: loading?.label ?? "Thinking…" });
    label.appendChild(text);
    getLoadingLabelRuntimeSync()?.applyLoadingLabel(text, config, "waiting");
    return label;
  }
  const container = createElement("div", "persona-flex persona-items-center persona-space-x-1 persona-h-5 persona-mt-2");

  for (let index = 0; index < 3; index++) {
    const dot = createElement("div", "persona-animate-typing persona-rounded-full persona-h-1.5 persona-w-1.5");
    dot.style.backgroundColor = "currentColor";
    dot.style.opacity = "0.4";
    dot.style.animationDelay = `${index * 250}ms`;
    container.appendChild(dot);
  }

  const srOnly = createElement("span", "persona-sr-only");
  srOnly.textContent = "Loading";

  container.appendChild(srOnly);

  return container;
};

/**
 * Render loading indicator with fallback chain:
 * 1. Custom renderer (if provided and returns non-null)
 * 2. Default typing indicator
 */
export const renderLoadingIndicatorWithFallback = (
  location: 'inline' | 'standalone',
  customRenderer?: LoadingIndicatorRenderer,
  widgetConfig?: import("../types").AgentWidgetConfig
): HTMLElement | null => {
  const context: LoadingIndicatorRenderContext = {
    config: widgetConfig ?? ({} as import("../types").AgentWidgetConfig),
    streaming: true,
    location,
    defaultRenderer: () => createTypingIndicator(widgetConfig)
  };

  return customRenderer?.(context) ?? createTypingIndicator(widgetConfig);
};

/**
 * Create an avatar element
 */
const createAvatar = (
  avatarConfig: AgentWidgetAvatarConfig,
  role: AgentWidgetMessageRole
): HTMLElement => {
  const avatar = createElement(
    "div",
    "persona-flex-shrink-0 persona-w-8 persona-h-8 persona-rounded-full persona-flex persona-items-center persona-justify-center persona-text-sm"
  );

  const isOperator = role === "operator";
  const avatarContent = role === "user"
    ? avatarConfig.userAvatar
    : isOperator
      ? avatarConfig.operatorAvatar
      : avatarConfig.assistantAvatar;

  // The operator avatar is a neutral outlined chip, apart from the solid
  // user and assistant fills.
  const applyFill = () => {
    avatar.classList.add(
      ...(isOperator
        ? ["persona-bg-persona-container", "persona-text-persona-text", "persona-border"]
        : [role === "user" ? "persona-bg-persona-accent" : "persona-bg-persona-primary", "persona-text-white"])
    );
  };

  if (avatarContent) {
    // Check if it's a URL or emoji/text
    if (avatarContent.startsWith("http") || avatarContent.startsWith("/") || avatarContent.startsWith("data:")) {
      const img = createElement("img") as HTMLImageElement;
      img.src = avatarContent;
      img.alt = role === "user" ? "User" : isOperator ? "Operator" : "Assistant";
      img.className = "persona-w-full persona-h-full persona-rounded-full persona-object-cover";
      avatar.appendChild(img);
    } else {
      // Emoji or text
      avatar.textContent = avatarContent;
      applyFill();
    }
  } else {
    // Default avatar
    avatar.textContent = role === "user" ? "U" : isOperator ? "O" : "A";
    applyFill();
  }

  return avatar;
};

/**
 * Create a timestamp element
 */
const createTimestamp = (
  message: AgentWidgetMessage,
  timestampConfig: AgentWidgetTimestampConfig,
  tagName: "div" | "span" = "div"
): HTMLElement => {
  const timestamp = createElement(
    tagName,
    "persona-text-xs persona-text-persona-muted"
  );

  const date = new Date(message.createdAt);
  
  if (timestampConfig.format) {
    timestamp.textContent = timestampConfig.format(date);
  } else {
    // Default format: HH:MM
    timestamp.textContent = date.toLocaleTimeString([], { 
      hour: "2-digit", 
      minute: "2-digit" 
    });
  }

  return timestamp;
};

/** Assistant color utilities an operator bubble replaces with its own tokens. */
const OPERATOR_DROPPED_CLASSES = new Set([
  "persona-bg-persona-container",
  "persona-text-persona-text",
  "persona-border-persona-message-border",
]);

/**
 * Get bubble classes based on layout preset
 */
export const getBubbleClasses = (
  role: AgentWidgetMessageRole,
  layout: AgentWidgetMessageLayoutConfig["layout"] = "bubble"
): string[] => {
  // Operator bubbles share the assistant's geometry per layout; only the role
  // class changes, and the colors come from the operator tokens instead.
  if (role === "operator") {
    const classes = getBubbleClasses("assistant", layout).flatMap((name) =>
      name === "persona-message-assistant-bubble"
        ? ["persona-message-operator-bubble"]
        : OPERATOR_DROPPED_CLASSES.has(name) ? [] : [name]
    );
    // Flat drops the assistant's fill, not the operator's: the operator keeps
    // its background, so it needs the minimal layout's side padding.
    return layout === "flat" ? [...classes, "persona-px-3"] : classes;
  }
  const baseClasses = ["persona-message-bubble"];

  switch (layout) {
    case "flat":
      // Flat layout: no bubble styling, just text
      if (role === "user") {
        baseClasses.push(
          "persona-message-user-bubble",
          "persona-ml-auto",
          "persona-text-persona-primary",
          "persona-py-2"
        );
      } else {
        baseClasses.push(
          "persona-message-assistant-bubble",
          "persona-text-persona-text",
          "persona-py-2"
        );
      }
      break;

    case "minimal":
      // Minimal layout: reduced padding and styling
      baseClasses.push(
        "persona-text-sm",
        "persona-leading-relaxed"
      );
      if (role === "user") {
        baseClasses.push(
          "persona-message-user-bubble",
          "persona-ml-auto",
          "persona-bg-persona-accent",
          "persona-text-white",
          "persona-px-3",
          "persona-py-2",
          "persona-rounded-lg"
        );
      } else {
        baseClasses.push(
          "persona-message-assistant-bubble",
          "persona-bg-persona-container",
          "persona-text-persona-text",
          "persona-px-3",
          "persona-py-2",
          "persona-rounded-lg"
        );
      }
      break;

    case "bubble":
    default:
      // Default bubble layout
      baseClasses.push(
        "persona-rounded-2xl",
        "persona-text-sm",
        "persona-leading-relaxed",
        "persona-shadow-sm"
      );
      if (role === "user") {
        baseClasses.push(
          "persona-message-user-bubble",
          "persona-ml-auto",
          "persona-bg-persona-accent",
          "persona-text-white",
          "persona-px-5",
          "persona-py-3"
        );
      } else {
        baseClasses.push(
          "persona-message-assistant-bubble",
          "persona-bg-persona-container",
          "persona-border",
          "persona-border-persona-message-border",
          "persona-text-persona-text",
          "persona-px-5",
          "persona-py-3"
        );
      }
      break;
  }

  return baseClasses;
};

/**
 * Padding / type each layout preset produces from its utility classes. Used as
 * the var() fallback for the `components.message.<role>` geometry tokens, so an
 * unset token renders exactly what the preset always did.
 */
const BUBBLE_GEOMETRY_DEFAULTS: Record<
  "bubble" | "flat" | "minimal",
  { padding: string; fontSize: string; lineHeight: string }
> = {
  bubble: { padding: "0.75rem 1.25rem", fontSize: "0.875rem", lineHeight: "1.75" },
  minimal: { padding: "0.5rem 0.75rem", fontSize: "0.875rem", lineHeight: "1.75" },
  flat: { padding: "0.5rem 0", fontSize: "inherit", lineHeight: "inherit" },
};

/**
 * Apply the `components.message.user/assistant` geometry tokens to a bubble.
 * Written inline as var() chains so the per-layout default stays the fallback;
 * the values are part of the rendered markup, so they survive transcript morphs.
 * Only configured keys are stamped: an inline pin for an unset token would
 * beat host-page CSS on the bubble at inline priority.
 */
export const applyBubbleGeometry = (
  bubble: HTMLElement,
  role: AgentWidgetMessage["role"],
  layout: AgentWidgetMessageLayoutConfig["layout"] = "bubble",
  tokens?: MessageGeometryTokens
): void => {
  if (!tokens) return;
  const key = role === "user" || role === "operator" ? role : "assistant";
  const preset = BUBBLE_GEOMETRY_DEFAULTS[layout ?? "bubble"] ?? BUBBLE_GEOMETRY_DEFAULTS.bubble;
  if (tokens.padding) {
    bubble.style.padding = `var(--persona-message-${key}-padding, ${preset.padding})`;
  }
  if (tokens.fontSize) {
    bubble.style.fontSize = `var(--persona-message-${key}-font-size, ${preset.fontSize})`;
  }
  if (tokens.lineHeight) {
    bubble.style.lineHeight = `var(--persona-message-${key}-line-height, ${preset.lineHeight})`;
  }
  if (tokens.fontFamily) {
    bubble.style.fontFamily = `var(--persona-message-${key}-font-family, inherit)`;
  }
};

/** `data-action` for a host-contributed action. Namespaced against built-ins. */
export const CUSTOM_MESSAGE_ACTION_PREFIX = "custom:";

export const customMessageActionDataAction = (id: string): string =>
  `${CUSTOM_MESSAGE_ACTION_PREFIX}${id}`;

/**
 * Host-contributed actions this message's role qualifies for. Assistant-only by
 * default, matching every built-in except edit and quote; an action reaches
 * operator messages only when its `roles` lists `"operator"`.
 */
export const customMessageActionsForRole = (
  actionsConfig: AgentWidgetMessageActionsConfig | undefined,
  role: AgentWidgetMessage["role"]
): AgentWidgetCustomMessageAction[] => {
  if (role !== "user" && role !== "assistant" && role !== "operator") return [];
  return (actionsConfig?.custom ?? []).filter((action) =>
    (action.roles ?? ["assistant"]).includes(role)
  );
};

/**
 * Create message action buttons (copy, upvote, downvote)
 *
 * This is a pure rendering function. It creates button elements with the
 * correct `data-action` attributes, icons, and CSS classes. All click
 * handling, vote state management, clipboard logic, and callback dispatch
 * is handled via event delegation in `ui.ts` so that handlers survive
 * idiomorph DOM morphing.
 */
export const createMessageActions = (
  message: AgentWidgetMessage,
  actionsConfig: AgentWidgetMessageActionsConfig,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _callbacks?: MessageActionCallbacks,
  eligibility?: MessageActionEligibility,
  adaptiveVisibility = false
): HTMLElement => {
  const showCopy = actionsConfig.showCopy ?? true;
  const showUpvote = actionsConfig.showUpvote ?? false;
  const showDownvote = actionsConfig.showDownvote ?? false;
  const showReadAloud = actionsConfig.showReadAloud ?? false;
  // Retry and edit are per-message: the config flag only opts in, the caller's
  // eligibility decides which single message actually shows the control.
  const showRegenerate =
    (actionsConfig.showRegenerate ?? false) && eligibility?.canRegenerate === true;
  const showEdit = (actionsConfig.showEdit ?? false) && eligibility?.canEdit === true;
  const showQuote = (actionsConfig.showQuote ?? false) && eligibility?.canQuote !== false;
  const customActions = customMessageActionsForRole(actionsConfig, message.role);

  // Don't render the container at all when no actions are visible
  if (
    !showCopy &&
    !showUpvote &&
    !showDownvote &&
    !showReadAloud &&
    !showRegenerate &&
    !showEdit &&
    !showQuote &&
    customActions.length === 0
  ) {
    const empty = createElement("div");
    empty.style.display = "none";
    empty.id = `actions-${message.id}`;
    empty.setAttribute("data-actions-for", message.id);
    return empty;
  }

  const visibility = actionsConfig.visibility ??
    (adaptiveVisibility && eligibility?.isLastAssistant ? "always" : "hover");
  const align = actionsConfig.align ?? "right";
  const layout = actionsConfig.layout ?? "pill-inside";

  // Map alignment to CSS class
  const alignClass = {
    left: "persona-message-actions-left",
    center: "persona-message-actions-center",
    right: "persona-message-actions-right",
  }[align];

  // Map layout to CSS class
  const layoutClass = {
    "pill-inside": "persona-message-actions-pill",
    "row-inside": "persona-message-actions-row",
  }[layout];

  const container = createElement(
    "div",
    `persona-message-actions persona-flex persona-items-center persona-gap-1 persona-mt-2 ${alignClass} ${layoutClass} ${
      visibility === "hover" ? "persona-message-actions-hover" : ""
    } ${adaptiveVisibility ? "persona-message-actions-adaptive" : ""}`
  );
  // Set id for idiomorph matching (prevents recreation on morph)
  container.id = `actions-${message.id}`;
  container.setAttribute("data-actions-for", message.id);

  const createActionButton = (
    iconName: string,
    label: string,
    dataAction: string
  ): HTMLButtonElement => {
    // Chromeless: the icon-btn base chrome is scoped under [data-persona-root]
    // and would outrank every .persona-message-action-btn state rule.
    const button = createIconButton({
      icon: iconName,
      label,
      size: 14,
      className: "persona-message-action-btn",
      chromeless: true,
    });
    button.setAttribute("data-action", dataAction);
    return button;
  };

  // Copy button
  if (showCopy) {
    container.appendChild(createActionButton("copy", "Copy message", "copy"));
  }

  // Read-aloud button. Click handling and play/pause/resume state are managed
  // in ui.ts (event delegation + ReadAloudController) so they survive morphs.
  if (showReadAloud) {
    container.appendChild(createActionButton("volume-2", "Read aloud", "read-aloud"));
  }

  // Upvote button
  if (showUpvote) {
    container.appendChild(createActionButton("thumbs-up", "Upvote", "upvote"));
  }

  // Downvote button
  if (showDownvote) {
    container.appendChild(createActionButton("thumbs-down", "Downvote", "downvote"));
  }

  // Regenerate: only the final retryable assistant turn (caller-decided).
  if (showRegenerate) {
    container.appendChild(
      createActionButton("refresh-cw", "Regenerate response", "regenerate")
    );
  }

  // Edit: only text-only user messages (caller-decided).
  if (showEdit) {
    container.appendChild(createActionButton("pencil", "Edit message", "edit"));
  }

  if (showQuote) {
    container.appendChild(createActionButton("quote", "Quote message", "quote"));
  }

  // Host-contributed actions close the row, in configuration order.
  for (const action of customActions) {
    container.appendChild(
      createActionButton(
        action.iconName ?? "ellipsis",
        action.label,
        customMessageActionDataAction(action.id)
      )
    );
  }

  return container;
};

/**
 * Options for creating a standard message bubble
 */
/**
 * Which history actions this ONE message may show. Retry lives on a single
 * assistant message and edit on qualifying user messages, so the decision is
 * made by the transcript (which sees the whole list), not per bubble.
 */
export type MessageActionEligibility = {
  /** Latest ordinary assistant message, including an in-flight response. */
  isLastAssistant?: boolean;
  canRegenerate?: boolean;
  canEdit?: boolean;
  canQuote?: boolean;
};

export type CreateStandardBubbleOptions = {
  /** Per-message gate for the regenerate/edit/quote controls. */
  actionEligibility?: MessageActionEligibility;
  /**
   * Custom loading indicator renderer for inline location
   */
  loadingIndicatorRenderer?: LoadingIndicatorRenderer;
  /**
   * Full widget config (needed for loading indicator context)
   */
  widgetConfig?: import("../types").AgentWidgetConfig;
  /**
   * Long user message collapse (`features.userMessageCollapse`). When set on
   * a user bubble, its content is wrapped in a clip region with the toggle.
   */
  userCollapse?: UserMessageCollapseRender;
};

/**
 * Create standard message bubble
 * Supports layout configuration for avatars, timestamps, and visual presets
 */
export const createStandardBubble = (
  message: AgentWidgetMessage,
  transform: MessageTransform,
  layoutConfig?: AgentWidgetMessageLayoutConfig,
  actionsConfig?: AgentWidgetMessageActionsConfig,
  actionCallbacks?: MessageActionCallbacks,
  options?: CreateStandardBubbleOptions
): HTMLElement => {
  const config = layoutConfig ?? {};
  const layout = config.layout ?? "bubble";
  const avatarConfig = config.avatar;
  const timestampConfig = config.timestamp;
  const showAvatar = avatarConfig?.show ?? false;
  const showTimestamp = timestampConfig?.show ?? false;
  const avatarPosition = avatarConfig?.position ?? "left";
  const timestampPosition = timestampConfig?.position ?? "below";

  // Create the bubble element
  const classes = getBubbleClasses(message.role, layout);
  const bubble = createElement("div", classes.join(" "));
  // Set id for idiomorph matching
  bubble.id = `bubble-${message.id}`;
  bubble.setAttribute("data-message-id", message.id);

  const isOperator = message.role === "operator";
  // The V5 operator is outlined, not filled, so every layout needs the border
  // style that only the bubble layout's classes carry.
  if (isOperator && options?.widgetConfig?.future?.v5Defaults === true) bubble.classList.add("persona-border");
  bubble.setAttribute(
    "data-persona-theme-zone",
    message.role === "user" ? "user-message" : isOperator ? "operator-message" : "assistant-message"
  );

  const defaultMessageTokens = resolveThemeDefaults(options?.widgetConfig?.future).components.message;
  const messageTokens = options?.widgetConfig?.theme?.components?.message;
  const darkMessageTokens = options?.widgetConfig?.darkTheme?.components?.message;
  applyBubbleGeometry(
    bubble,
    message.role,
    layout,
    message.role === "user"
      ? { ...defaultMessageTokens.user, ...messageTokens?.user, ...darkMessageTokens?.user }
      : isOperator
        ? { ...defaultMessageTokens.operator, ...messageTokens?.operator, ...darkMessageTokens?.operator }
        : { ...defaultMessageTokens.assistant, ...messageTokens?.assistant, ...darkMessageTokens?.assistant }
  );

  // Apply component-level color overrides via CSS variables. Flat clears the
  // user and assistant fills; an operator keeps its own background token so it
  // stays apart from the assistant prose on the same side.
  if (layout === "flat" && !isOperator) {
    bubble.style.backgroundColor = "transparent";
    bubble.style.color =
      message.role === "user"
        ? "var(--persona-primary, var(--persona-text))"
        : "var(--persona-message-assistant-text, var(--persona-text))";
  } else if (message.role === "user") {
    bubble.style.backgroundColor = 'var(--persona-message-user-bg, var(--persona-accent))';
    bubble.style.color = 'var(--persona-message-user-text, white)';
  } else if (message.role === "assistant") {
    bubble.style.backgroundColor = 'var(--persona-message-assistant-bg, var(--persona-container))';
    bubble.style.color = 'var(--persona-message-assistant-text, var(--persona-text))';
  } else if (isOperator) {
    bubble.style.backgroundColor = 'var(--persona-components-message-operator-background, var(--persona-container))';
    bubble.style.color = 'var(--persona-components-message-operator-text, var(--persona-text))';
  }

  if (isOperator) {
    const authorLabel = createElement("div", "persona-message-author persona-text-xs persona-font-medium persona-mb-1");
    const configuredLabel = options?.widgetConfig?.copy?.operatorLabel;
    const operatorLabel = typeof configuredLabel === "string" && configuredLabel.trim()
      ? configuredLabel.trim() : "Human operator";
    const displayName = typeof message.author?.displayName === "string"
      ? message.author.displayName.trim() : "";
    authorLabel.textContent = displayName ? `${displayName} · ${operatorLabel}` : operatorLabel;
    // A visible name is also the accessible name for the human-authored group.
    bubble.setAttribute("role", "group");
    bubble.setAttribute("aria-label", authorLabel.textContent);
    bubble.appendChild(authorLabel);
  }

  const imageParts = getMessageImageParts(message);
  const messageContentText = message.content?.trim() ?? "";
  const isImageOnlyFallbackMessage =
    imageParts.length > 0 && messageContentText === IMAGE_ONLY_MESSAGE_FALLBACK_TEXT;
  const shouldHideTextUntilPreviewFails = isImageOnlyFallbackMessage;

  const streamAnimation = resolveStreamAnimation(
    options?.widgetConfig?.features?.streamAnimation
  );
  const streamPluginOverrides =
    options?.widgetConfig?.features?.streamAnimation?.plugins;
  const streamPlugin =
    message.role === "assistant" && streamAnimation.type !== "none"
      ? resolveStreamAnimationPlugin(streamAnimation.type, streamPluginOverrides)
      : null;
  // Stay in "streaming-animated" mode while the plugin reports in-flight
  // work for this message: e.g. glyph-cycle's tick loops still walking
  // through the tail after the last token arrived. Without this, the final
  // non-animated render rips out the cycling spans mid-animation.
  const pluginStillAnimating =
    message.role === "assistant" &&
    streamPlugin?.isAnimating?.(message) === true;
  const streamAnimationActive =
    message.role === "assistant" &&
    streamPlugin !== null &&
    (Boolean(message.streaming) || pluginStillAnimating);

  if (streamAnimationActive && streamPlugin?.bubbleClass) {
    bubble.classList.add(streamPlugin.bubbleClass);
  }

  // Add message content
  const contentDiv = document.createElement("div");
  contentDiv.classList.add("persona-message-content");

  // While streaming, lock table column widths (see widget.css) so rows append
  // without per-chunk horizontal reflow. The class is dropped on the final,
  // non-streaming render, relaxing tables to natural content-fit widths.
  if (message.streaming) {
    contentDiv.classList.add("persona-content-streaming");
  }

  if (streamAnimationActive && streamPlugin) {
    if (streamPlugin.containerClass) {
      contentDiv.classList.add(streamPlugin.containerClass);
    }
    contentDiv.style.setProperty("--persona-stream-step", `${streamAnimation.speed}ms`);
    contentDiv.style.setProperty("--persona-stream-duration", `${streamAnimation.duration}ms`);
  }

  const bufferedContent = streamAnimationActive
    ? applyStreamBuffer(
        message.content ?? "",
        streamAnimation.buffer,
        streamPlugin,
        message,
        Boolean(message.streaming)
      )
    : (message.content ?? "");

  // Feeds the two text branches below; the inline-mention branch calls the
  // transform itself with a placeholder-bearing source built from
  // `contentSegments` instead of `bufferedContent`. Kept lazy so that branch
  // doesn't pay for output it never reads. `bufferedContent` stays eager —
  // the streaming skeleton logic reads it.
  const computeAnimatedContent = (): string => {
    const transformedContent = transform({
      text: bufferedContent,
      message,
      streaming: Boolean(message.streaming),
      raw: message.rawContent
    });
    if (streamAnimationActive && streamPlugin?.wrap === "char") {
      return wrapStreamAnimation(transformedContent, "char", message.id, {
        skipTags: streamPlugin.skipTags,
      });
    }
    if (streamAnimationActive && streamPlugin?.wrap === "word") {
      return wrapStreamAnimation(transformedContent, "word", message.id, {
        skipTags: streamPlugin.skipTags,
      });
    }
    return transformedContent;
  };

  let textContentDiv: HTMLElement | null = null;

  if (shouldHideTextUntilPreviewFails) {
    textContentDiv = document.createElement("div");
    textContentDiv.innerHTML = computeAnimatedContent();
    textContentDiv.style.display = "none";
    contentDiv.appendChild(textContentDiv);
  } else if (message.contentSegments?.length) {
    // Inline-mention user bubble: prose runs through the same transform
    // pipeline as every other bubble (markdown/postprocess/sanitize apply);
    // mention slots ride through as placeholders and come back as atomic
    // tokens. The resolved context reached the model via `llmContent`.
    renderSegmentsWithTransform(
      contentDiv,
      message.contentSegments,
      (source) =>
        transform({
          text: source,
          message,
          streaming: Boolean(message.streaming),
          raw: message.rawContent,
        }),
      options?.widgetConfig?.contextMentions?.renderMentionToken
    );
  } else {
    contentDiv.innerHTML = computeAnimatedContent();
  }

  if (
    streamAnimationActive &&
    streamPlugin?.useCaret &&
    !shouldHideTextUntilPreviewFails &&
    messageContentText
  ) {
    const caret = createStreamCaret();
    // Caret must sit on the same line as the final char. Markdown wraps text
    // in block elements (<p>, <li>, <pre>), so appending to contentDiv would
    // drop the caret onto a fresh line. Tuck it after the last char/word span,
    // or fall back to the last block when no spans exist yet.
    const spans = contentDiv.querySelectorAll(
      ".persona-stream-char, .persona-stream-word"
    );
    const lastSpan = spans[spans.length - 1];
    if (lastSpan?.parentNode) {
      lastSpan.parentNode.insertBefore(caret, lastSpan.nextSibling);
    } else {
      const lastChild = contentDiv.lastElementChild;
      if (lastChild) {
        lastChild.appendChild(caret);
      } else {
        contentDiv.appendChild(caret);
      }
    }
  }

  // Usung <span>, not <div> for timestamp 
  // because a block child of a <p> gets re-parented outside of it on re-render (idiomorph).
  if (showTimestamp && timestampPosition === "inline" && message.createdAt) {
    const timestamp = createTimestamp(message, timestampConfig!, "span");
    timestamp.classList.add("persona-timestamp-inline");
    const lastBlock = contentDiv.lastElementChild;
    if (lastBlock) {
      lastBlock.appendChild(timestamp);
    } else {
      contentDiv.appendChild(timestamp);
    }
  }

  // Everything a long user message can spill (attachments, chips, text) goes
  // in the collapse region; the timestamp and actions stay outside the clip.
  const userCollapse = message.role === "user" ? options?.userCollapse : undefined;
  const collapseRegion = userCollapse ? createUserCollapseRegion(message.id, userCollapse.maxHeight) : null;
  const contentParent = collapseRegion ?? bubble;

  if (imageParts.length > 0) {
    const imagePreviews = createMessageImagePreviews(
      imageParts,
      !shouldHideTextUntilPreviewFails && Boolean(messageContentText),
      () => {
        if (shouldHideTextUntilPreviewFails && textContentDiv) {
          textContentDiv.style.display = "";
        }
      }
    );

    if (imagePreviews) {
      contentParent.appendChild(imagePreviews);
    } else if (shouldHideTextUntilPreviewFails && textContentDiv) {
      textContentDiv.style.display = "";
    }
  }

  const audioParts = getMessageAudioParts(message);
  if (audioParts.length > 0) {
    const audioPreviews = createMessageAudioPreviews(audioParts);
    if (audioPreviews) {
      contentParent.appendChild(audioPreviews);
    }
  }

  const videoParts = getMessageVideoParts(message);
  if (videoParts.length > 0) {
    const videoPreviews = createMessageVideoPreviews(videoParts);
    if (videoPreviews) {
      contentParent.appendChild(videoPreviews);
    }
  }

  const fileParts = getMessageFileParts(message);
  if (fileParts.length > 0) {
    const filePreviews = createMessageFilePreviews(fileParts);
    if (filePreviews) {
      contentParent.appendChild(filePreviews);
    }
  }

  // Context mention pills (read-only) for the sent user bubble. Skipped in inline
  // mode — the tokens render within the prose (above) instead of a separate row.
  const mentionChips = message.contentSegments?.length
    ? null
    : createMessageMentionChips(message.contextMentions);
  if (mentionChips) {
    contentParent.appendChild(mentionChips);
  }

  contentParent.appendChild(contentDiv);
  if (collapseRegion && userCollapse) {
    bubble.appendChild(collapseRegion);
    applyUserCollapseState(bubble, collapseRegion, message.id, userCollapse);
  }

  // Add timestamp below if configured
  if (showTimestamp && timestampPosition === "below" && message.createdAt) {
    const timestamp = createTimestamp(message, timestampConfig!);
    timestamp.classList.add("persona-mt-1");
    bubble.appendChild(timestamp);
  }

  // Resolve the stop-reason notice (if any). Only assistant messages can
  // carry a stop reason worth surfacing.
  const stopReasonNoticeText =
    message.role === "assistant"
      ? resolveStopReasonNoticeText(
          message.stopReason,
          options?.widgetConfig?.copy?.stopReasonNotice
        )
      : null;

  // Add typing indicator (or skeleton placeholder) for streaming assistant
  // messages. Check the buffered content: a plugin's `bufferContent` may
  // hold back the first N chars (e.g. glyph-cycle waits for 50 chars), during
  // which the bubble would otherwise appear empty.
  //
  // When the `"line"` buffer strategy is paired with the skeleton placeholder,
  // the skeleton trails below any already-revealed content to hint that more
  // lines are on the way. It disappears on stream completion.
  if (message.streaming && message.role === "assistant") {
    const hasVisibleContent = Boolean(bufferedContent && bufferedContent.trim());
    const skeletonEnabled = streamAnimation.placeholder === "skeleton";
    const trailSkeleton =
      skeletonEnabled && streamAnimation.buffer === "line" && hasVisibleContent;
    if (!hasVisibleContent) {
      if (skeletonEnabled) {
        bubble.appendChild(createSkeletonPlaceholder());
      } else {
        const indicator = renderLoadingIndicatorWithFallback(
          'inline',
          options?.loadingIndicatorRenderer,
          options?.widgetConfig
        );
        if (indicator) {
          bubble.appendChild(indicator);
        }
      }
    } else if (trailSkeleton) {
      bubble.appendChild(createSkeletonPlaceholder());
    }
  }

  // Append the stop-reason notice for non-natural completions. When the
  // assistant produced no text (the `max_tool_calls` empty-bubble symptom),
  // hide the empty content div so the notice carries the entire bubble
  // instead of trailing under a blank space.
  if (stopReasonNoticeText && message.stopReason && !message.streaming) {
    if (!messageContentText) {
      contentDiv.style.display = "none";
    }
    bubble.appendChild(createStopReasonNotice(message.stopReason, stopReasonNoticeText));
  }

  // Add message actions for assistant and operator messages (only when not
  // streaming and has content). Operator rows get copy and opted-in custom
  // actions only: feedback, read-aloud and regenerate are about model output.
  const shouldShowActions =
    (message.role === "assistant" || isOperator) &&
    !message.streaming &&
    // A voice caption (the voice model's own speech) is display-only.
    !message.voiceCaption &&
    (messageContentText.length > 0 || (isOperator && customMessageActionsForRole(actionsConfig, "operator").length > 0)) &&
    actionsConfig?.enabled !== false;

  // User bubbles carry actions only when this message is edit- or quote-eligible;
  // nothing else about a user bubble changes.
  const eligibility = options?.actionEligibility;
  const shouldShowUserActions =
    message.role === "user" &&
    !message.streaming &&
    actionsConfig?.enabled !== false &&
    ((actionsConfig?.showEdit === true && eligibility?.canEdit === true) ||
      (actionsConfig?.showQuote === true && eligibility?.canQuote === true) ||
      // A custom action opted into `"user"` brings the row up on its own.
      customMessageActionsForRole(actionsConfig, "user").length > 0);

  if ((shouldShowActions || shouldShowUserActions) && actionsConfig) {
    const actions = createMessageActions(
      message,
      shouldShowUserActions
        ? // A user bubble never hosts the assistant-only controls.
          { ...actionsConfig, showCopy: false, showUpvote: false, showDownvote: false, showReadAloud: false }
        : isOperator
          ? {
              ...actionsConfig,
              showCopy: messageContentText.length > 0 && actionsConfig.showCopy !== false,
              showUpvote: false,
              showDownvote: false,
              showReadAloud: false,
              showRegenerate: false,
              showEdit: false,
              showQuote: false,
            }
          : actionsConfig,
      actionCallbacks,
      eligibility,
      options?.widgetConfig?.future?.v5Defaults === true && actionsConfig.visibility === undefined
    );
    bubble.appendChild(actions);
  }

  // If no avatar needed, return bubble directly
  if (!showAvatar || message.role === "system") {
    return bubble;
  }

  // Create wrapper with avatar
  const wrapper = createElement(
    "div",
    `persona-message-with-avatar persona-flex persona-gap-2 ${
      message.role === "user" ? "persona-flex-row-reverse" : ""
    }`
  );

  const avatar = createAvatar(avatarConfig!, message.role);

  if (avatarPosition === "right" || (avatarPosition === "left" && message.role === "user")) {
    wrapper.append(bubble, avatar);
  } else {
    wrapper.append(avatar, bubble);
  }

  return wrapper;
};

/**
 * Create bubble with custom renderer support
 * Uses custom renderer if provided in layout config, otherwise falls back to standard bubble
 */
export const createBubbleWithLayout = (
  message: AgentWidgetMessage,
  transform: MessageTransform,
  layoutConfig?: AgentWidgetMessageLayoutConfig,
  actionsConfig?: AgentWidgetMessageActionsConfig,
  actionCallbacks?: MessageActionCallbacks,
  options?: CreateStandardBubbleOptions
): HTMLElement => {
  const config = layoutConfig ?? {};

  // Check for custom renderers
  if (message.role === "user" && config.renderUserMessage) {
    return config.renderUserMessage({
      message,
      config: {} as any, // Will be populated by caller
      streaming: Boolean(message.streaming)
    });
  }

  if (message.role === "assistant" && config.renderAssistantMessage) {
    return config.renderAssistantMessage({
      message,
      config: {} as any, // Will be populated by caller
      streaming: Boolean(message.streaming)
    });
  }

  // Fall back to standard bubble
  return createStandardBubble(message, transform, layoutConfig, actionsConfig, actionCallbacks, options);
};
